import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useOrganizationList } from "~/features/admin/organizations/hooks";
import { toOrganizationRowList } from "~/features/admin/organizations/utils/transformers";
import { v2Route } from "~/v2/routes";
import { OrganizationsPresenter } from "./organizations.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function OrganizationsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.organizations);

  const { data, refetch } = useOrganizationList();
  const rows = useMemo(() => toOrganizationRowList(data.organizations), [data.organizations]);

  // The text input is driven by local state for instant feedback. The URL is only a
  // background mirror (sharable / restored on reload + back-forward): React Router v7 applies
  // navigation updates in a transition, so binding the input's value straight to `params.search`
  // makes fast typing lag. We adopt the URL value into local state only on a *genuine external*
  // change (back/forward, shared link): the URL changed since we last saw it AND it differs from
  // what we have locally. Our own writes — which the URL only lags toward — match local, so they
  // never clobber the in-progress value.
  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next ? next : undefined, page: undefined }));
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <OrganizationsPresenter
      rows={rows}
      search={search}
      onSearchChange={onSearchChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onRefresh={onRefresh}
    />
  );
}
